import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Language {
    code: string;
    name: string;
    flag: string;
}

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    private readonly STORAGE_KEY = 'elfie-language-preference';
    private readonly defaultLanguage = 'en';

    private readonly supportedLanguages: Language[] = [
        { code: 'en', name: 'English', flag: 'us' },
        { code: 'es', name: 'Español', flag: 'es' }
    ];

    private currentLanguageSubject: BehaviorSubject<string>;
    public currentLanguage$: Observable<string>;

    constructor() {
        const initialLanguage = this.determineInitialLanguage();
        this.currentLanguageSubject = new BehaviorSubject<string>(initialLanguage);
        this.currentLanguage$ = this.currentLanguageSubject.asObservable();
    }

    private determineInitialLanguage(): string {
        // First check localStorage
        const storedLang = localStorage.getItem(this.STORAGE_KEY);
        if (storedLang && this.isSupported(storedLang)) {
            return storedLang;
        }

        // Then check browser language
        const browserLang = navigator.language.split('-')[0];
        if (this.isSupported(browserLang)) {
            this.saveLanguagePreference(browserLang);
            return browserLang;
        }

        // Default to English
        this.saveLanguagePreference(this.defaultLanguage);
        return this.defaultLanguage;
    }

    private isSupported(lang: string): boolean {
        return this.supportedLanguages.some(supported => supported.code === lang);
    }

    private saveLanguagePreference(lang: string): void {
        localStorage.setItem(this.STORAGE_KEY, lang);
        if (this.currentLanguageSubject) {
            this.currentLanguageSubject.next(lang);
        }
    }

    getSupportedLanguages(): Language[] {
        return this.supportedLanguages;
    }

    getCurrentLanguage(): string {
        return this.currentLanguageSubject.value;
    }

    changeLanguage(languageCode: string): void {
        if (!this.isSupported(languageCode)) {
            console.warn(`Language ${languageCode} is not supported`);
            return;
        }

        // If we're already on this language, don't do anything
        const currentLang = this.getCurrentLanguage();
        if (currentLang === languageCode) {
            return;
        }

        this.saveLanguagePreference(languageCode);
        // Reload the page to refresh all content
        window.location.reload();
    }

    setLanguageWithoutReload(languageCode: string): boolean {
        if (!this.isSupported(languageCode)) {
            console.warn(`Language ${languageCode} is not supported`);
            return false;
        }

        const currentLang = this.getCurrentLanguage();
        if (currentLang === languageCode) {
            return false; // No change needed
        }

        localStorage.setItem(this.STORAGE_KEY, languageCode);
        return true; // Language was changed
    }

    getCurrentLanguageInfo(): Language {
        const currentLang = this.getCurrentLanguage();
        return this.supportedLanguages.find(lang => lang.code === currentLang) || this.supportedLanguages[0];
    }
}